<template>
  <div
    :id="id"
    class="store-teaser"
    :class="{
      'store-teaser--favorite': favoriteStore,
      'store-teaser--info-window': infoWindow,
    }"
  >
    <fn-button
      v-if="infoWindow"
      class="st-close"
      plain
      @click="$emit('close')"
    >
      <fn-icon
        icon="cross"
        size="xxs"
        color="orientation-green"
      />
    </fn-button>
    <div
      v-if="favoriteStore"
      class="st-notifications"
    >
      <span class="n-item">
        {{ $t('storeListing.teaser.favorite') }}
      </span>
    </div>
    <div class="st-info">
      <fn-link
        inline
        wrapper
        @click="$emit('showDetails')"
      >
        <fn-headline
          class="st-name"
          type="h2"
          size="h4"
          color="green-dark"
        >
          {{ store.displayName }}
          <fn-picture
            v-if="store?.posType?.logo?.url"
            :id="id + '-image'"
            :src="store.posType.logo.url"
            :width="store.posType.logo.width"
            :height="store.posType.logo.height"
            :alt="store.posType.code ? store.posType.code : ''"
            class="image--pos-type"
          />
        </fn-headline>

        <fn-store-address
          v-if="store.address && Object.keys(store.address).length"
          :id="id + '-address'"
          class="st-address"
          :address="store.address"
        />
      </fn-link>
    </div>
    <div class="st-route">
      <fn-paragraph
        v-if="!infoWindow && store.formattedDistance"
        class="st-route-distance"
      >
        {{ $t('store.nearbyStores.distance', { distance: store.formattedDistance }) }}
      </fn-paragraph>

      <fn-button
        class="st-route-calculate-btn"
        icon="route"
        tertiary-alt
        full-width
        @click="$emit('calcRoute')"
      >
        {{ $t('store.route.buttonShort') }}
      </fn-button>
    </div>

    <fn-store-status
      v-if="store.openingHours"
      class="st-status"
      :store="store"
    />

    <div
      v-if="promotionsPresent || eventsPresent"
      class="st-offers"
    >
      <div
        v-if="promotionsPresent"
        class="offer"
      >
        <fn-icon
          class="offer-icon"
          icon="circle-percent"
        />
        {{ $t('storeListing.teaser.promotions') }}
      </div>

      <div
        v-if="eventsPresent"
        class="offer"
      >
        <fn-icon
          class="offer-icon"
          icon="calendar-heart"
        />
        {{ $t('storeListing.teaser.events') }}
      </div>
    </div>
    <div
      v-if="store.salonAvailable || store.activetAvailable"
      class="st-services"
    >
      <fn-link
        v-if="store.salonAvailable"
        class="service service--salon"
        no-arrow
        :to="store.address?.postalCode ? `/salon?store=${store.address.postalCode}#map` : null"
        target="_blank"
      >
        {{ $t('storeListing.teaser.salon') }}
      </fn-link>
      <fn-link
        v-if="store.activetAvailable"
        class="service service--activet"
        no-arrow
        :href="activetUrl"
        target="_blank"
      >
        {{ $t('storeListing.teaser.activet') }}
      </fn-link>
    </div>
  </div>
</template>

<script lang="ts">
export default defineComponent({
  props: {
    id: {
      type: String,
    },
    store: {
      type: Object,
      required: true,
    },
    favoriteStore: {
      type: Boolean,
    },
    infoWindow: {
      type: Boolean,
    },
  },
  emits: ['calcRoute', 'showDetails', 'close'],
  computed: {
    activetUrl() {
      return this.store.activetUrl ?? 'https://www.activet.eu/';
    },

    promotionsPresent() {
      return this.store.promotionPageComponents && this.store.promotionPageComponents.length;
    },

    eventsPresent() {
      return this.store.eventPageComponents && this.store.eventPageComponents.length;
    },
  },
});
</script>

<style lang="scss" scoped>
.store-teaser {
  padding: $pad;
  background-color: $color-general-background-neutral-default;
  display: flex;
  flex-wrap: wrap;

  &.store-teaser--favorite {
    background-color: palette(background, green);
  }

  &.store-teaser--info-window {
    background-color: $color-general-background-neutral-default;

    @include bp(m, max) {
      flex-direction: column;
    }

    .st-notifications .n-item {
      background-color: palette(grey, lighter);
    }

    .st-info {
      @include bp(m) {
        padding-right: calc(#{$pad * 1.5} + 120px);
      }
    }

    .st-route {
      margin-top: $pad;
      order: 6;
      flex: 0;

      @include bp(m) {
        position: absolute;
        margin-top: 0;
        top: 50%;
        right: $pad;
        transform: translateY(-50%);
      }
    }
  }

  .st-close {
    position: absolute;
    top: math.div($space, 4);
    right: $pad;
  }

  .st-notifications {
    margin-bottom: math.div($pad, 2);
    width: 100%;

    .n-item {
      color: palette(orientation, green);
      background-color: $color-general-background-neutral-default;
      padding: math.div($pad, 4) math.div($pad, 2);
      border-radius: map-get($border-radiuses, 'l');
      font-size: map-get($font-sizes-paragraph, 's');
      font-weight: map-get($font-weights, 'bold');
    }
  }

  .st-info {
    flex-basis: 50%;
    flex-grow: 1;
    padding-right: math.div($pad, 2);

    .st-name {
      margin-bottom: math.div($pad, 2);

      .picture.image--pos-type {
        display: inline-block;
        max-width: 33px;
      }
    }

    .st-address {
      font-size: map-get($font-sizes-headings, 'xxs');
    }
  }

  .st-route {
    flex: 0 0 120px;
    display: flex;
    flex-direction: column;

    .st-route-distance {
      margin-bottom: math.div($space, 2);
    }
  }

  .st-status {
    width: 100%;
    margin-top: math.div($space, 2);

    :deep(.ss-title) {
      font-size: map-get($font-sizes-headings, 'xxs');
    }
  }

  .st-offers {
    width: 100%;
    display: flex;
    flex-direction: column;
    row-gap: math.div($pad, 4);
    margin-top: $pad;

    .offer {
      color: palette(grey, dark);
      line-height: 1.5;
      font-size: map-get($font-sizes-headings, 'xxs');

      .offer-icon {
        margin-right: math.div($space, 4);
      }
    }
  }

  .st-services {
    width: 100%;
    display: flex;
    column-gap: math.div($pad, 2);
    margin-top: $pad;

    .service {
      padding: math.div($pad, 4) math.div($pad, 2);
      border-radius: map-get($border-radiuses, 'l');
      color: $color-general-text-inverse-default;
      font-size: map-get($font-sizes-headings, 'xxs');
      font-weight: map-get($font-weights, 'bold');

      &.service--salon {
        background-color: palette(orientation, blue);
      }

      &.service--activet {
        background-color: palette(brand, activet);
      }
    }
  }
}
</style>
